import { useEmbedContext } from '@thoughtspot/blink-context';
import { useTranslation } from '@thoughtspot/i18n';
import { ActionMenu, } from '@thoughtspot/radiant-react/widgets/action-menu';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal } from '@thoughtspot/radiant-react/widgets/layout';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import _ from 'lodash';
import React from 'react';
import { handleClick } from "../../utils/nav-config.util";
import { CustomMenuItem } from '../custom-menu-item/custom-menu-item';
import styles from './application-switcher.module.scss';
export var QuickLink;
(function (QuickLink) {
    QuickLink["SEARCH_DATA"] = "search-data";
    QuickLink["LIVEBOARDS"] = "liveboards";
    QuickLink["ANSWERS"] = "answers";
})(QuickLink || (QuickLink = {}));
export var ApplicationSwitcherTestIds;
(function (ApplicationSwitcherTestIds) {
    ApplicationSwitcherTestIds["ApplicationSwitcherButton"] = "application-switcher-button";
    ApplicationSwitcherTestIds["ApplicationSwitcherMenu"] = "application-switcher-menu";
    ApplicationSwitcherTestIds["AdminIcon"] = "application-switcher-icon-community";
})(ApplicationSwitcherTestIds || (ApplicationSwitcherTestIds = {}));
const isApplicationSelected = (config, activeApplicationID) => {
    return !!activeApplicationID && config?.id === activeApplicationID;
};
const getNavLinkCustomContent = (queryBuilderString) => {
    return (React.createElement(Horizontal, { vAlignContent: "center", className: styles.customContentNav },
        React.createElement(Typography, { variant: "body-normal", "data-testid": queryBuilderString, className: styles.navLinkCustomContentText }, queryBuilderString)));
};
export const ApplicationSwitcher = ({ configs, activeApplicationID, sendMixpanelEvent, openQueryBuilder, openAnswerListPage, openLiveboardListPage, getCurrentPathByLocationHash, isReactShellEnabled, disableQuickLinks = false, customQuickLinks = [], }) => {
    const { t } = useTranslation();
    const embedConfig = useEmbedContext();
    const { hiddenHomeLeftNavItems } = embedConfig;
    const [isSwitcherMenuVisible, setIsSwitcherMenuVisible] = React.useState(false);
    const [isInitialRender, setIsInitialRender] = React.useState(true);
    const applicationSwitcherButtonRef = React.useRef(null);
    const toggleSwitcherMenu = () => {
        setIsSwitcherMenuVisible(prevState => !prevState);
    };
    const quickLinkItems = [
        {
            id: 'searchData',
            label: t('gnav.appSwitcher.SEARCH_DATA'),
            action: openQueryBuilder,
            path: '/#/insights/answer',
            hide: _.includes(hiddenHomeLeftNavItems, QuickLink.SEARCH_DATA),
        },
        {
            id: 'viewAllLiveboards',
            label: t('gnav.appSwitcher.ALL_LIVEBOARDS'),
            action: openLiveboardListPage,
            path: '/#/insights/home/liveboards',
            hide: _.includes(hiddenHomeLeftNavItems, QuickLink.LIVEBOARDS),
        },
        {
            id: 'viewAllAnswers',
            label: t('gnav.appSwitcher.ALL_ANSWERS'),
            action: openAnswerListPage,
            path: '/#/insights/home/answers',
            hide: _.includes(hiddenHomeLeftNavItems, QuickLink.ANSWERS),
        },
    ];
    const visibleQuickLinkItems = quickLinkItems.filter(item => !item.hide);
    const visibleCustomQuickLinkItems = customQuickLinks?.length > 0
        ? customQuickLinks.filter(item => !item.hide)
        : [];
    const menuItemGroups = [
        {
            title: 'application list',
            items: configs.map(config => ({
                id: config.name,
                isSelected: isApplicationSelected(config, activeApplicationID),
                customContent: React.createElement(CustomMenuItem, { config: config }),
                href: `/#${config.target.routerURI ?? config.target.uri}`,
                onClick: () => {
                    const isCurrentApplication = isApplicationSelected(config, activeApplicationID);
                    handleClick(config, isCurrentApplication, getCurrentPathByLocationHash, isReactShellEnabled);
                    toggleSwitcherMenu();
                    sendMixpanelEvent(config.name);
                },
            })),
        },
        ...(visibleQuickLinkItems.length > 0 && !disableQuickLinks
            ? [
                {
                    title: 'navigation links',
                    items: visibleQuickLinkItems.map(item => ({
                        id: item.id,
                        customContent: getNavLinkCustomContent(item.label),
                        href: item.path,
                        onClick: () => {
                            toggleSwitcherMenu();
                            item.action();
                        },
                    })),
                },
            ]
            : []),
        ...(visibleCustomQuickLinkItems.length > 0
            ? [
                {
                    title: 'navigation links',
                    items: visibleCustomQuickLinkItems.map(item => ({
                        id: item.id,
                        customContent: getNavLinkCustomContent(item.label),
                        href: item.path,
                        onClick: () => {
                            toggleSwitcherMenu();
                            item.action();
                        },
                    })),
                },
            ]
            : []),
    ];
    const SwitcherMenu = (React.createElement(ActionMenu, { id: "appSwitcherMenu", menuItemGroups: menuItemGroups, show: isSwitcherMenuVisible, "data-testid": ApplicationSwitcherTestIds.ApplicationSwitcherMenu, className: styles.actionMenu }));
    React.useEffect(() => {
        if (isInitialRender) {
            setIsInitialRender(false);
            return;
        }
        if (!isSwitcherMenuVisible) {
            requestAnimationFrame(() => {
                applicationSwitcherButtonRef?.current?.focus();
            });
            return;
        }
        const firstItemId = menuItemGroups?.[0]?.items?.[0]?.id;
        if (firstItemId) {
            requestAnimationFrame(() => {
                document.getElementById(firstItemId)?.focus();
            });
        }
    }, [isSwitcherMenuVisible]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { onClick: toggleSwitcherMenu, icon: IconID.NINE_SQUARES, isSelected: isSwitcherMenuVisible, type: "secondary", "data-testid": ApplicationSwitcherTestIds.ApplicationSwitcherButton, id: ApplicationSwitcherTestIds.ApplicationSwitcherButton, "aria-expanded": isSwitcherMenuVisible, "aria-label": t('application_menu_aria_label'), "aria-haspopup": "menu", buttonRef: applicationSwitcherButtonRef }),
        React.createElement(Popover, { "aria-labelledby": ApplicationSwitcherTestIds.ApplicationSwitcherButton, "aria-hidden": !isSwitcherMenuVisible, "aria-orientation": "vertical", targetRef: applicationSwitcherButtonRef, align: "bottom-end", className: styles.popover, isVisible: isSwitcherMenuVisible, content: SwitcherMenu, onClickOutside: () => {
                setIsSwitcherMenuVisible(false);
                applicationSwitcherButtonRef.current.focus();
            }, onEscape: () => {
                setIsSwitcherMenuVisible(false);
                applicationSwitcherButtonRef.current.focus();
            } })));
};
//# sourceMappingURL=application-switcher.js.map